/**
 * WF-Next-Btn - Composable Continue/Submit button for wizard forms
 *
 * Auto-detects the last step and becomes "Submit" button.
 * Dispatches 'wf:nav-next' event that bubbles to wizard-form.
 *
 * @example
 * <wf-step data-step="1">
 *   <wf-input name="fullName" required></wf-input>
 *   <wf-next-btn></wf-next-btn>
 * </wf-step>
 *
 * @example
 * <wf-other label="Others, please specify:">
 *   <wf-next-btn inline label="Next →"></wf-next-btn>
 * </wf-other>
 */

import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { NavigationButtonBase } from './base/navigation-button-base.js';
import { wfNextBtnStyles } from './wf-next-btn.styles.js';
import './wf-badge.js';

// Re-export NavStateDetail for backward compatibility
export type { NavStateDetail } from './base/navigation-button-base.js';

@customElement('wf-next-btn')
export class WfNextBtn extends NavigationButtonBase {
  static styles = wfNextBtnStyles;

  // ============================================
  // Properties
  // ============================================

  /**
   * Custom label (overrides auto "Continue"/"Submit")
   */
  @property({ type: String })
  label?: string;

  /**
   * Force action type (auto-detected by default)
   */
  @property({ type: String })
  action?: 'next' | 'submit';

  /**
   * Show "Enter ↵" badge (default: true)
   */
  @property({ type: Boolean, attribute: 'show-shortcut' })
  showShortcut = true;

  /**
   * Inline styling for placement next to inputs
   */
  @property({ type: Boolean })
  inline = false;

  /**
   * Disabled state
   */
  @property({ type: Boolean })
  disabled = false;

  // ============================================
  // Abstract Implementation
  // ============================================

  protected _getEventName(): string {
    return 'wf:nav-next';
  }

  // ============================================
  // Event Handlers
  // ============================================

  private _handleClick = (): void => {
    if (this.disabled || this._isSubmitting) {
      return;
    }
    this._dispatchNavEvent();
  };

  // ============================================
  // Helpers
  // ============================================

  private _getButtonLabel(): string {
    if (this.label) {
      return this.label;
    }
    // Inline buttons (in wf-other) always show "Continue", not "Submit"
    const isSubmit = !this.inline && (this.action === 'submit' || (this.action !== 'next' && this._isLastStep));
    return isSubmit ? 'Submit' : 'Continue';
  }

  private _isSubmitAction(): boolean {
    // Inline buttons (in wf-other) always act as "next", not "submit"
    return !this.inline && (this.action === 'submit' || (this.action !== 'next' && this._isLastStep));
  }

  // ============================================
  // Render
  // ============================================

  override render() {
    const buttonLabel = this._getButtonLabel();
    const isDisabled = this.disabled || this._isSubmitting;

    return html`
      <button
        type="button"
        class="wf-btn wf-btn-next"
        ?disabled="${isDisabled}"
        @click="${this._handleClick}"
        data-testid="wf-next-btn"
      >
        ${this._isSubmitting
          ? html`<span class="wf-loading"></span>`
          : html`
              ${buttonLabel}
              ${this.showShortcut
                ? html`
                    <span class="wf-btn-shortcut ${this.inline ? 'wf-btn-shortcut-inline' : ''}">
                      <wf-badge variant="button" shortcut="Enter ↵"></wf-badge>
                    </span>
                  `
                : nothing}
            `}
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-next-btn': WfNextBtn;
  }
}
